import { logger } from "src/core/globals";
import {
  MarkerType,
  Message,
  QueryType,
  Request
} from "src/gen/schema/syncfollow/syncfollow";
import { unreachable } from "src/utils/switch";

import { Stream } from "./core/stream";
import { StreamReducer, StreamTransformer } from "./core/types";
import { StreamManager } from "./streamManager";
import { SyncFollowSocket as Socket } from "./syncFollowSocket";

export abstract class SyncFollowStreamManager extends StreamManager {
  private pruneTimeoutId: NodeJS.Timeout | null = null;
  protected sock: Socket = new Socket();
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  protected streams: Map<string, Stream<Message, any>> = new Map();

  addStream<TData>(
    message: Message,
    transformer: StreamTransformer<Message, TData>,
    reducer: StreamReducer<TData>
  ): Stream<Message, TData> | undefined {
    const stream = new Stream<Message, TData>(message, transformer, reducer);
    /**
     * When a new Stream is created we need
     * to add a listener for when it is no
     * longer needed. at that point the
     * Stream Manager needs to clean up and
     * remove the unused stream
     */
    stream.on(Stream.END, this.cleanup, this);
    /**
     * Add the new stream to the streams
     * Map.
     */
    this.streams.set(stream.syncId, stream);
    if (this.sock) {
      /**
       * Send the request to the websocket
       * to start the syncAndFollow.
       */
      this.initStream(stream);
    }
    return stream;
  }

  cleanup(s: Stream<Message, Message>) {
    /**
     * Before cleaning up the stream
     * make sure that there are no
     * followers (stream.clients). If there
     * are clients we will wait until
     * they are all removed before cleaning
     * up the stream.
     */
    if (s.followers() === 0) {
      const stopMessage = Message.fromPartial({
        request: Request.fromPartial({
          syncId: s.syncId,
          query: QueryType.Stop
        })
      });
      /* send the websocket the stop query */
      this.sendMessage(stopMessage);

      /* removed the registry entry for this Stream */
      this.streams.delete(s.syncId);
      /**
       * if there are no more
       * streams, close the
       * connection
       */
      if (this.streams.size === 0) {
        this.prune();
      }
    }
  }

  prune() {
    /**
     * This will delay pruning
     * for 60 seconds from the last
     * prune event. That way
     * if another Stream gets
     * added within that 60 seconds
     * we will reuse the existing
     * Socket.
     */
    if (this.pruneTimeoutId) {
      clearTimeout(this.pruneTimeoutId);
    }
    this.pruneTimeoutId = setTimeout(() => {
      if (this.streams.size === 0) {
        this.fireEvent(StreamManager.PRUNE, this);
        this.sock.close();
      }
    }, 60000);
  }

  handleMessage(msg: Message) {
    if (msg.error) {
      /**
       * Need to add some king of global
       * Error handler here. I don't think
       * it makes sense to try and make
       * the websocket client handle this.
       */
      const stream = this.streams.get(msg.error.syncId);
      stream?.handleError(msg);
      return;
    }

    const logMarkers = window.cnc?.settings?.get("logSyncFollowMarkers");

    if (msg.data) {
      const { marker = MarkerType.None, syncId } = msg.data;
      if (syncId) {
        const stream = this.streams.get(syncId);
        if (stream) {
          if (logMarkers) {
            logger.info(new Date(), MarkerType[marker]);
          }

          switch (marker) {
            case MarkerType.StartOfSync:
              /**
               * According to backend the data
               * should be reset and rebuilt
               * if you get StartOfSync
               * marker.
               */
              stream.marker = marker;
              stream.data = [];
              break;
            case MarkerType.ResumeFailed:
              /**
               * if ResumeFailed, try sending
               * the request again and re-syncing
               */
              this.initStream(stream);
              break;
            case MarkerType.EndOfSync:
            case MarkerType.StartOfFollow:
              stream.marker = marker;
              stream.handleMeta();
              break;
            case MarkerType.UpToDate:
              /**
               * When we are up to date makes sure
               * we send the data. This way calls will
               * not get stuck in limbo
               */
              stream.handleMessage();
              break;
            case MarkerType.None:
              /**
               * Data messages have no maker,
               * This will to process
               * the message and let the
               * stream pass data to its
               * consumer(s)
               */
              stream.handleMessage(msg);
              break;
            case MarkerType.TotalCount:
              stream.handleMeta(msg);
              break;
            case MarkerType.UNRECOGNIZED:
            case MarkerType.StillAlive:
            case MarkerType.SyncClosed:
              /**
               * We ignore these messages.
               */
              break;
            default:
              /**
               * This will cause a compile
               * time error if a new MarkerType
               * is added and not handled.
               */
              unreachable(marker);
          }
        }
      }
    }
  }

  initStream<TData>(stream: Stream<Message, TData>) {
    /**
     * Setting the syncId in the request
     * alters the object which will cause
     * issues. Instead, to we can keep track
     * of what messages are for which consumers
     * I'm making it part of the message
     * to the websocket only and keeping
     * the original request unchanged.
     */
    const request = Message.create({
      request: { ...stream.getRequest(), syncId: stream.syncId }
    });
    this?.sock?.send(request);
  }

  handleReconnect() {
    /**
     * Finish reconnect logic
     */
    this.streams.forEach((stream: Stream<Message, Message>) => {
      this.initStream(stream);
    });
  }
}
